import {
  getRecipeNameWithTrackingPrefix,
  isFreshExtraProduct,
  isFreshRecipe,
  isLitterProduct,
} from "@api-clients/subscriptions";
import { IPostCheckoutCharge } from "@api-clients/charges";

import {
  SubscriptionItemType,
  SubscriptionItemVariantValue,
} from "@/primary-models";

import {
  IGTMRechargeEvent,
  IGTMPurchaseEvent,
  IGTMPurchaseEventItem,
  IGTMRechargeEventProduct,
} from "../../types/gtm";

function getItemCategory(itemType: SubscriptionItemType): string {
  if (isFreshRecipe(itemType)) {
    return "ACTIVE_RECIPE";
  }
  if (isFreshExtraProduct(itemType)) {
    return "ADD_ON";
  }
  if (isLitterProduct(itemType)) {
    return "MAIN_SUBSCRIPTION";
  }
  return "SERVICE_OR_FEE";
}

function getItemId(itemType: SubscriptionItemType, variant: SubscriptionItemVariantValue): string {
  if (variant === SubscriptionItemVariantValue.DEFAULT) {
    return itemType.toLowerCase();
  }
  return `${itemType.toLowerCase()}-${variant.toLowerCase()}`;
}

function buildProducts(charge: IPostCheckoutCharge, shipping: string): IGTMRechargeEventProduct[] {
  const products = charge.chargeItems.map((item) => ({
    brand: "KatKin",
    id: getItemId(item.subscriptionItemType, item.subscriptionItemVariantValue),
    name: getRecipeNameWithTrackingPrefix(item.subscriptionItemType),
    price: item.originalTotalPrice,
    productId: item.subscriptionItemId.toString(),
    quantity: item.quantity,
    variant: 0,
  }));

  products.push({
    brand: "Katkin Club",
    id: "NDF",
    name: "DPD Delivery Fee",
    price: shipping,
    productId: "delivery_fee",
    quantity: 1,
    variant: 0,
  });

  return products;
}

export function buildGtmDataUA(
  charge: IPostCheckoutCharge,
  katkinCustomerId: string,
  flow?: string,
): IGTMRechargeEvent {
  const transactionId = charge.chargeId.toString();
  const revenue = charge.totalPriceDiscounted;
  const tax = charge.taxAmountDiscounted;
  const shipping = charge.deliveryFeeDiscounted;
  const affiliation = globalThis.window === undefined ? "katkin.co.uk" : globalThis.window.location.hostname;

  const products = buildProducts(charge, shipping);

  return {
    event: "rc_rechargeTransaction",
    customerEmail: charge.email,
    customerFirstName: charge.firstName,
    customerLastName: charge.lastName,
    customerPostcode: charge.shippingAddress?.zip,
    customerPhone: charge.shippingAddress?.phone,
    katkinCustomerId,
    kkcoupon: charge.discountCode,
    kktransactionId: transactionId,
    kkrevenue: revenue,
    ecommerce: {
      userId: katkinCustomerId,
      currencyCode: "GBP",
      purchase: {
        actionField: {
          action: "purchase",
          affiliation,
          coupon: charge.discountCode,
          id: transactionId,
          list: flow || "",
          option: charge.discountCode ? charge.discountCode : "Free Shipping",
          revenue,
          shipping,
          tax,
        },
        products,
      },
    },
  };
}

function buildItems(charge: IPostCheckoutCharge, shipping: string): IGTMPurchaseEventItem[] {
  const items = charge.chargeItems.map((item, index) => ({
    affiliation: "KatKin",
    index,
    item_brand: "KatKin",
    item_category: getItemCategory(item.subscriptionItemType),
    item_id: getItemId(item.subscriptionItemType, item.subscriptionItemVariantValue),
    item_name: getRecipeNameWithTrackingPrefix(item.subscriptionItemType),
    item_variant: `${item.subscriptionItemVariantValue}`,
    price: item.originalUnitPrice,
    quantity: item.quantity,
  }));

  if (Number(shipping) !== 0) {
    items.push({
      affiliation: "Katkin Club",
      index: items.length,
      item_brand: "KatKin",
      item_category: "SERVICE_OR_FEE",
      item_id: "NDF",
      item_name: "DPD Delivery Fee",
      item_variant: "NO_DELIVERY_FEE",
      price: shipping,
      quantity: 1,
    });
  }

  return items;
}

export function buildGtmDataGA4(
  charge: IPostCheckoutCharge,
): IGTMPurchaseEvent {
  const transactionId = charge.chargeId.toString();
  const value = charge.totalPriceDiscounted;
  const shipping = charge.deliveryFeeDiscounted;
  const tax = charge.taxAmountDiscounted;

  const items = buildItems(charge, shipping);

  return {
    event: "purchase",
    ecommerce: {
      coupon: charge.discountCode,
      currency: "GBP",
      items,
      shipping,
      tax,
      transaction_id: transactionId,
      value,
    },
  };
}
